import { useCallback, useContext, useEffect, useRef } from 'react';
import { AdditionalInfoView, AdditionalInfoType } from '~/additional-infos/types';
import { AdditionalInfosContext } from '~/additional-infos';
import { AgencyTypeGlobalKeys } from '~/common/global-attributes';

type ProQaSessionState = {
    wasClosed: boolean;
    incidentNumber?: string | number;
};

const INITIAL_STATE: Record<AgencyTypeGlobalKeys, ProQaSessionState> = {
    FIRE: { wasClosed: false },
    EMS: { wasClosed: false },
    POLICE: { wasClosed: false },
};

function mapDisciplineToAgencyType(discipline: string): AgencyTypeGlobalKeys | null {
    const normalizedDiscipline = discipline.toLowerCase();
    switch (normalizedDiscipline) {
        case 'medical':
            return 'EMS';
        case 'fire':
            return 'FIRE';
        case 'police/sheriff':
            return 'POLICE';
        default:
            return null;
    }
}

/**
 * Tracks ProQA session state by monitoring additional info messages.
 * Returns whether a session was closed for a given agency type and incident.
 * Automatically resets state when sessions are reopened or incidents change.
 */
export function useProQaSessionTracker(currentIncidentNumber?: string | number) {
    const additionalInfosContext = useContext(AdditionalInfosContext);
    const sessionStateRef = useRef<Record<AgencyTypeGlobalKeys, ProQaSessionState>>({
        ...INITIAL_STATE,
    });
    const previousIncidentNumberRef = useRef<string | number | undefined>(currentIncidentNumber);
    const additionalInfos: AdditionalInfoView[] | undefined = Array.from(
        additionalInfosContext?.list ?? []
    );

    useEffect(
        function resetSessionState() {
            if (
                currentIncidentNumber &&
                previousIncidentNumberRef.current !== currentIncidentNumber
            ) {
                sessionStateRef.current = { ...INITIAL_STATE };
                previousIncidentNumberRef.current = currentIncidentNumber;
            }
        },
        [currentIncidentNumber]
    );

    useEffect(
        function trackProQaSessionState() {
            if (!additionalInfos || !currentIncidentNumber) {
                return;
            }

            for (const info of additionalInfos) {
                if (info.type !== AdditionalInfoType.PROQA) {
                    continue;
                }

                const sessionClosedMatch = info.text.match(
                    /<(Medical|Fire|Police\/Sheriff)\s+ProQA\s+session\s+closed>/i
                );

                if (sessionClosedMatch) {
                    const discipline = sessionClosedMatch[1];
                    const agencyType = mapDisciplineToAgencyType(discipline);

                    if (agencyType) {
                        sessionStateRef.current[agencyType] = {
                            wasClosed: true,
                            incidentNumber: currentIncidentNumber,
                        };
                    }
                }
            }
        },
        [additionalInfos, currentIncidentNumber]
    );

    const checkIfSessionWasClosed = useCallback(
        (agencyType: AgencyTypeGlobalKeys): boolean => {
            const state = sessionStateRef.current[agencyType];
            return state.wasClosed && state.incidentNumber === currentIncidentNumber;
        },
        [currentIncidentNumber]
    );

    return { checkIfSessionWasClosed };
}
